import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useTgBootstrap } from './TgBootstrap'
import { useAppStore } from '@/store/useAppStore'
import { LinkEmailAutoPromptTg } from '@features/link-email/ui/LinkEmailAutoPromptTg'
import { LinkTelegramConfirmScreen } from '@features/link-telegram/ui/LinkTelegramConfirmScreen'
import { TelegramAuthConfirmScreen } from '@features/telegram-auth'
import {
  useCategories,
  useCards,
  useFavorites,
  useAnnouncements,
  useSubscription,
} from '@/hooks/useApi'
import LoadingScreen from '@/components/LoadingScreen/LoadingScreen'
import FortuneLoadingScreen from '@/components/FortuneLoadingScreen/FortuneLoadingScreen'
import ScrollToTop from '@/components/ScrollToTop'
import Layout from '@/components/Layout/Layout'
import HomePage from '@/pages/HomePage/HomePage'
import ProfilePage from '@/pages/ProfilePage/ProfilePage'
import CardPage from '@/pages/CardPage'
import TestVideoPage from '@/pages/TestVideoPage'
import SubscribePage from '@/pages/SubscribePage'
import CoursesPage from '@/pages/CoursesPage/CoursesPage'
import CourseDetailPage from '@/pages/CourseDetailPage/CourseDetailPage'
import LessonPage from '@/pages/LessonPage/LessonPage'
import FavoritesPage from '@/pages/FavoritesPage/FavoritesPage'
import FlavorsPage from '@/pages/FlavorsPage/FlavorsPage'
import CalculatorPage from '@/pages/CalculatorPage/CalculatorPage'
import FortuneWheelPage from '@/pages/FortuneWheelPage/FortuneWheelPage'
import TermsPage from '@pages/TermsPage/TermsPage'
import PrivacyPage from '@pages/PrivacyPage/PrivacyPage'
import { PaymentStatusModal } from '@features/payments'
import { GracePeriodPopup } from '@/pages/ProfilePage/components/SubscriptionSection/GracePeriodPopup'
import { FEATURES } from '@/config/features'

export const TgAppShell = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoading, startParam } = useTgBootstrap()
  const { isLoading: storeLoading, authStatus } = useAppStore()
  const [showInitialLoading, setShowInitialLoading] = useState(true)

  const startParamHandledRef = useRef(false)
  useEffect(() => {
    if (!startParam) return
    if (startParamHandledRef.current) return
    if (startParam.startsWith('link_code_')) {
      startParamHandledRef.current = true
      navigate(`/link-telegram/confirm?token=${encodeURIComponent(startParam)}`, { replace: true })
    } else if (startParam.startsWith('telegram_auth_')) {
      startParamHandledRef.current = true
      navigate(`/telegram-auth/confirm?token=${encodeURIComponent(startParam)}`, { replace: true })
    }
  }, [startParam, navigate])

  const isAuthComplete = authStatus === 'success'
  useCategories(isAuthComplete)
  useCards()
  useFavorites()
  useAnnouncements()
  useSubscription()

  useEffect(() => {
    const timer = setTimeout(() => setShowInitialLoading(false), 3500)
    return () => clearTimeout(timer)
  }, [])

  const handleLoadingComplete = () => setShowInitialLoading(false)

  const shouldShowLoading = showInitialLoading || isLoading || storeLoading
  const isFortuneWheelRoute =
    FEATURES.fortuneWheel &&
    (startParam === 'wheel' || location.pathname.startsWith('/fortune-wheel'))
  const shouldApplySafeArea =
    !location.pathname.startsWith('/card/') &&
    !location.pathname.startsWith('/subscribe') &&
    !location.pathname.match(/^\/courses\/\d/)

  return (
    <div className={shouldApplySafeArea ? 'safe-area-inset' : ''}>
      <div style={{ visibility: shouldShowLoading ? 'hidden' : 'visible' }}>
        <ScrollToTop />
        <Routes>
          <Route path="card/:cardId" element={<CardPage />} />
          <Route path="courses/:courseId" element={<CourseDetailPage />} />
          <Route path="courses/:courseId/lesson/:lessonId" element={<LessonPage />} />
          <Route path="test-video" element={<TestVideoPage />} />
          <Route path="subscribe" element={<SubscribePage />} />
          <Route path="calculator/:cardId?" element={<CalculatorPage />} />
          <Route
            path="fortune-wheel"
            element={
              FEATURES.fortuneWheel ? <FortuneWheelPage /> : <Navigate to="/" replace />
            }
          />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="link-telegram/confirm" element={<LinkTelegramConfirmScreen />} />
          <Route path="telegram-auth/confirm" element={<TelegramAuthConfirmScreen />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="favorites" element={<FavoritesPage />} />
            <Route path="flavors" element={<FlavorsPage />} />
          </Route>
        </Routes>
      </div>

      <LinkEmailAutoPromptTg />
      <PaymentStatusModal />
      <GracePeriodPopup />

      {shouldShowLoading &&
        (isFortuneWheelRoute ? (
          <FortuneLoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ) : (
          <LoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ))}
    </div>
  )
}
